import { Item, ParentItem } from "../schema/item.js";
import { compressToUTF16, decompressFromUTF16 } from "./compress.js";

export {
  loadVerseData,
  setVerseData,
  loadVerseOAData,
  setVerseOAData,
  loadPMData,
  setPMData,
  loadVersion,
  setVersion,
  getLocalStorageUsage,
};

const getLocalStorageUsage = (): number => {
  let total = 0;
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key) {
      const value = localStorage.getItem(key) || "";
      total += key.length + value.length;
    }
  }
  // 1文字あたり2バイトとして計算
  return total * 2;
};

// --- ヘルパー関数: QRデータのエンコード／デコード --- //
/**
 * 保存時に、ビット文字列を 16 進数表現に変換します。
 * ビット数が 4 の倍数でない場合、先頭にゼロでパディングし、
 * 元のビット長を冒頭に付与した形式 "originalLength:hexData" として返します。
 */
const encodeQr = (bitString: string): string => {
  const originalLength = bitString.length;
  const paddedLength = Math.ceil(originalLength / 4) * 4;
  const padCount = paddedLength - originalLength;
  const paddedBitString = "0".repeat(padCount) + bitString;
  let hexString = "";
  for (let i = 0; i < paddedBitString.length; i += 4) {
    const nibble = paddedBitString.slice(i, i + 4);
    hexString += parseInt(nibble, 2).toString(16);
  }
  return `${originalLength}:${hexString}`;
};

/**
 * 取得時に、エンコードされた文字列（"originalLength:hexData"）を元のビット文字列に戻します。
 */
const decodeQr = (encoded: string): string => {
  const parts = encoded.split(":");
  if (parts.length !== 2) {
    // エンコード済みでなければそのまま返す
    return encoded;
  }
  const originalLength = parseInt(parts[0], 10);
  const hexString = parts[1];
  let paddedBitString = "";
  for (let i = 0; i < hexString.length; i++) {
    const nibble = hexString[i];
    let binaryNibble = parseInt(nibble, 16).toString(2);
    binaryNibble = binaryNibble.padStart(4, "0");
    paddedBitString += binaryNibble;
  }
  const padCount = paddedBitString.length - originalLength;
  return paddedBitString.slice(padCount);
};

// --- 子要素の所持状態の集計（保存時には除外、読込時に再計算） --- //
const calcChildCheckState = (parent: ParentItem) => {
  const total = parent.children?.length ?? 0;
  const owned = parent.children?.filter((c) => c.check === true).length ?? 0;
  return {
    owned,
    total,
    comp: total > 0 ? owned === total : false,
  };
};

// --- 既存のデータ整形関数 --- //
const processParentItem = (item: ParentItem): ParentItem => ({
  ...item,
  rowState: item.rowState ?? { selected: false, open: false },
  // 子要素を処理する際、qr のデコードを実施
  children: item.children.map(processChildItem),
  // childCheckState が無い場合は再計算
  childCheckState: item.childCheckState ?? calcChildCheckState(item),
});

const processChildItem = (item: Item): Item => ({
  ...item,
  rowState: item.rowState ?? { selected: false },
  // qr プロパティが存在する場合、保存時にエンコードされた文字列を元に戻す
  ...(item.qr ? { qr: decodeQr(item.qr) } : {}),
});

/**
 * 保存用に、不要な rowState を除去しつつ、
 * qr プロパティがあればエンコードしておく。
 */
const removeRowState = (item: Item): object => {
  // 保存しないフィールド: rowState, element, childCheckState（派生）, および children は別途処理
  const { rowState, element, childCheckState, children, qr, ...rest } =
    item as any;
  const newQr = qr ? encodeQr(qr) : undefined;
  return {
    ...rest,
    ...(newQr !== undefined ? { qr: newQr } : {}),
    children: children ? children.map(removeRowState) : undefined,
  };
};

// --- 省キー保存のためのミニファイ／復元 --- //
// 子要素: id(i) name(n) check(k) sec(s) star(r) brand(b) term(t) itemID(d) parentID(p) url(u) point(o) qr(q) available(a)
// 親要素: id(i) name(n) sec(s) star(r) brand(b) itemID(d) qr(q) children(c) available(a)
type MinChild = {
  i: string;
  n?: string;
  k?: boolean;
  s?: string;
  r?: string;
  b?: string;
  t?: string;
  d?: string;
  p?: string;
  u?: string;
  o?: string;
  q?: string;
  a?: string;
  y?: string;
  e?: MinChild[];
  z?: string;
};
type MinParent = {
  i: string;
  n: string;
  s?: string;
  r?: string;
  b?: string;
  d?: string;
  q?: string;
  a?: string;
  c: MinChild[];
};

const toMinChild = (it: Item): MinChild => {
  const m: MinChild = { i: it.id };
  if (it.name) m.n = it.name;
  if (typeof it.check !== "undefined") m.k = it.check;
  if (it.sec) m.s = it.sec;
  if (it.star) m.r = it.star;
  if (it.brand) m.b = it.brand as any;
  if (it.term) m.t = it.term as any;
  if (it.itemID) m.d = it.itemID;
  if (it.parentID) m.p = it.parentID;
  if (it.url) m.u = it.url;
  if (it.point) m.o = it.point;
  if (it.qr) m.q = encodeQr(it.qr);
  if ((it as any).available) m.a = (it as any).available;
  if (it.itemType) m.y = it.itemType;
  if (it.setItems) m.e = it.setItems.map(toMinChild);
  if (it.setQR) m.z = encodeQr(it.setQR);
  return m;
};

const fromMinChild = (m: MinChild): Item => {
  const it: Partial<Item> = {
    id: m.i,
    name: m.n ?? "",
    check: m.k ?? false,
    sec: m.s ?? "",
    star: m.r ?? "",
    brand: (m.b ?? "") as any,
    term: (m.t ?? "") as any,
    itemID: m.d ?? "",
    parentID: m.p ?? "",
    url: m.u ?? "",
    point: m.o ?? "",
    qr: m.q ? decodeQr(m.q) : "",
    // UI 関連は読み出し時に復元
    element: document.createElement("tr"),
    rowState: { selected: false },
  };
  if (m.a) (it as any).available = m.a;
  if (m.y) it.itemType = m.y as any;
  if (m.e) it.setItems = m.e.map(fromMinChild);
  if (m.z) it.setQR = decodeQr(m.z);
  return it as Item;
};

const toMinParent = (p: ParentItem): MinParent => {
  const m: MinParent = {
    i: p.id,
    n: p.name,
    c: (p.children ?? []).map(toMinChild),
  };
  if (p.sec) m.s = p.sec;
  if (p.star) m.r = p.star;
  if ((p as any).brand) m.b = (p as any).brand;
  if (p.itemID) m.d = p.itemID;
  if (p.qr) m.q = encodeQr(p.qr);
  if ((p as any).available) m.a = (p as any).available;
  return m;
};

const fromMinParent = (m: MinParent): ParentItem => {
  const p: Partial<ParentItem> = {
    id: m.i,
    name: m.n,
    sec: m.s ?? "",
    star: m.r ?? "",
    brand: (m.b ?? "") as any,
    itemID: m.d ?? "",
    qr: m.q ? decodeQr(m.q) : "",
    children: (m.c ?? []).map(fromMinChild),
    element: document.createElement("tr"),
    rowState: { open: false, selected: false },
  };
  if (m.a) (p as any).available = m.a;
  // 読み出し時に子の所持集計を復元
  (p as any).childCheckState = calcChildCheckState(p as ParentItem);
  return p as ParentItem;
};

const isMinified = (arr: any[]): arr is MinParent[] => {
  // 子配列キーが c で、親 ID が i を持つ形式ならミニファイ済みと判断
  return (
    Array.isArray(arr) &&
    (arr.length === 0 || (arr[0] && "i" in arr[0] && "c" in arr[0]))
  );
};

const normalizeLoadedParents = (raw: any[]): ParentItem[] => {
  if (isMinified(raw)) {
    return (raw as MinParent[]).map(fromMinParent);
  }
  // 旧形式: rowState だけ除外され、その他は素のキー
  return (raw as ParentItem[]).map((it) => {
    // element が欠損／無効な場合でも UI 参照のため生成
    const withUI = {
      ...it,
      element: document.createElement("tr"),
      children: (it.children ?? []).map((ch: any) => ({
        ...ch,
        element: document.createElement("tr"),
      })),
    } as ParentItem;
    return processParentItem(withUI);
  });
};

const loadVerseData = () => {
  const raw = localStorage.getItem("VerseData");
  if (!raw) return [] as ParentItem[];
  try {
    let json = raw;
    if (json.startsWith(COMP_PREFIX)) {
      const body = json.slice(COMP_PREFIX.length);
      const dec = decompressFromUTF16(body);
      json = dec ?? "[]";
    }
    const parsed = JSON.parse(json);
    return normalizeLoadedParents(parsed);
  } catch {
    return [] as ParentItem[];
  }
};

const setVerseData = (data: ParentItem[]) => {
  // 保存時に UI／派生情報を落として省キー化
  const minified = data.map(toMinParent);
  try {
    const json = JSON.stringify(minified);
    const compressed = compressToUTF16(json);
    localStorage.setItem("VerseData", COMP_PREFIX + compressed);
  } catch (error) {
    if (error instanceof Error) {
      alert(`VerseData の保存に失敗しました: ${error.message}`);
    } else {
      alert("VerseData の保存に失敗しました。");
    }
  }
};

const loadVerseOAData = () => {
  const raw = localStorage.getItem("VerseOAData");
  if (!raw) return [] as ParentItem[];
  try {
    let json = raw;
    if (json.startsWith(COMP_PREFIX)) {
      const body = json.slice(COMP_PREFIX.length);
      const dec = decompressFromUTF16(body);
      json = dec ?? "[]";
    }
    const parsed = JSON.parse(json);
    return normalizeLoadedParents(parsed);
  } catch {
    return [] as ParentItem[];
  }
};

const setVerseOAData = (data: ParentItem[]) => {
  const minified = data.map(toMinParent);
  try {
    const json = JSON.stringify(minified);
    const compressed = compressToUTF16(json);
    localStorage.setItem("VerseOAData", COMP_PREFIX + compressed);
  } catch (error) {
    if (error instanceof Error) {
      alert(`VerseOAData の保存に失敗しました: ${error.message}`);
    } else {
      alert("VerseOAData の保存に失敗しました。");
    }
  }
};

const loadPMData = () => {
  const raw = localStorage.getItem("PMData");
  if (!raw) return [] as ParentItem[];
  try {
    let json = raw;
    if (json.startsWith(COMP_PREFIX)) {
      const body = json.slice(COMP_PREFIX.length);
      const dec = decompressFromUTF16(body);
      json = dec ?? "[]";
    }
    const parsed = JSON.parse(json);
    return normalizeLoadedParents(parsed);
  } catch {
    return [] as ParentItem[];
  }
};

const setPMData = (data: ParentItem[]) => {
  const minified = data.map(toMinParent);
  try {
    const json = JSON.stringify(minified);
    const compressed = compressToUTF16(json);
    localStorage.setItem("PMData", COMP_PREFIX + compressed);
  } catch (error) {
    if (error instanceof Error) {
      alert(`PMData の保存に失敗しました: ${error.message}`);
    } else {
      alert("PMData の保存に失敗しました。");
    }
  }
};
const COMP_PREFIX = "LZ16:";

const loadVersion = () => {
  return localStorage.getItem("APP_VERSION") || "no-version";
};

const setVersion = (version: string) => {
  try {
    localStorage.setItem("APP_VERSION", version);
  } catch (error) {
    if (error instanceof Error) {
      alert(`APP_VERSION の保存に失敗しました: ${error.message}`);
    } else {
      alert("APP_VERSION の保存に失敗しました。");
    }
  }
};
